import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaWhatsapp, FaMobileAlt, FaQrcode, FaHeart, FaArrowLeft, FaCopy, FaCheck, FaCrown, FaCreditCard, FaSpinner,
} from "react-icons/fa";
import { WHATSAPP_NUMBER } from "../config/contact";
import { useAuth } from "../contexts/AuthContext";
import { crearCheckoutPlan } from "../services/apiService";
import { irA } from "../utils/navegacion";

// Datos de pago — por ahora el dinero entra solo por Nequi (llave Bre-B). Es
// una cuenta DISTINTA del WhatsApp de contacto (src/config/contact.js, que
// comparte la landing). Debe coincidir con la llave del QR de public/qr-nequi.jpeg.
const NEQUI_NUMBER = "3148520270";

// Planes pagos — deben coincidir con backend/src/config/plans.js. Si cambias
// un número allá, cámbialo aquí también (no se leen del backend a propósito:
// esta pantalla es pública y no requiere sesión, así que no hay de dónde
// pedirlos con auth).
const PAID_PLANS = {
  BASICO: { label: "Básico", priceMonthlyCOP: 10000, priceCOP: 60000, priceLifetimeCOP: 250000, durationDays: 180, maxProducts: 150 },
  PRO: { label: "Pro", priceMonthlyCOP: 20000, priceCOP: 120000, priceLifetimeCOP: 500000, durationDays: 180, maxProducts: 500 },
};

// Precio y texto según el ciclo de pago elegido (mensual, 6 meses o de por vida).
const precioPlan = (p, ciclo) =>
  ciclo === "lifetime" ? p.priceLifetimeCOP : ciclo === "mensual" ? p.priceMonthlyCOP : p.priceCOP;
const duracionTexto = (p, ciclo) =>
  ciclo === "lifetime" ? "sin vencimiento" : ciclo === "mensual" ? "1 mes" : `${p.durationDays / 30} meses`;

// Ciclo de esta pantalla → periodo que entiende el backend (config/plans.js).
const PERIODO_DE_CICLO = { mensual: "MENSUAL", semestral: "SEMESTRAL", lifetime: "VITALICIO" };

// Pago en línea (Wompi, vía el servicio central de pagos de Tyndall): el plan
// se activa solo al aprobarse. Solo lo hace un admin del negocio, con sesión;
// esta pantalla también es pública, así que a los demás se les explica qué hacer.
const PagoEnLinea = ({ planKey, plan, ciclo, price }) => {
  const { user } = useAuth();
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const esAdmin = user && (user.rol === "admin_compania" || user.rol === "super_admin_sistema");

  const pagar = async () => {
    setCargando(true);
    setError("");
    try {
      const { urlPago } = await crearCheckoutPlan(planKey, PERIODO_DE_CICLO[ciclo]);
      irA(urlPago);
    } catch (e) {
      setError(e.message || "No pudimos abrir el pago. Intenta de nuevo o paga por Nequi.");
      setCargando(false);
    }
  };

  return (
    <div className="bg-emerald-50 border border-emerald-100 rounded-[2rem] p-6 md:p-8 mb-10 text-center">
      <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-emerald-500 text-white flex items-center justify-center text-xl">
        <FaCreditCard />
      </div>
      <h3 className="font-black text-xl text-gray-900 mb-2">Paga en línea y se activa al instante</h3>
      <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
        Tarjeta, PSE, Nequi o Bancolombia, en el pago seguro de Wompi. Apenas se
        apruebe, tu plan {plan.label} queda activo, sin enviar comprobantes.
      </p>
      {esAdmin ? (
        <>
          <button
            onClick={pagar}
            disabled={cargando}
            className="inline-flex items-center gap-3 bg-emerald-500 text-white font-black px-8 py-4 rounded-2xl hover:bg-emerald-600 transition-all active:scale-95 shadow-lg shadow-emerald-500/20 disabled:opacity-60"
          >
            {cargando ? <FaSpinner className="animate-spin" /> : <FaCreditCard />}
            Pagar ${price.toLocaleString("es-CO")} con Wompi
          </button>
          {error && <p role="alert" className="text-sm text-red-600 font-medium mt-4">{error}</p>}
        </>
      ) : user ? (
        <p className="text-sm font-bold text-gray-700">
          Pídele al administrador de tu negocio que pague el plan desde su cuenta.
        </p>
      ) : (
        <Link
          to="/login"
          className="inline-flex items-center gap-3 bg-emerald-500 text-white font-black px-8 py-4 rounded-2xl hover:bg-emerald-600 transition-all active:scale-95"
        >
          Inicia sesión para pagar en línea
        </Link>
      )}
    </div>
  );
};

const Logo = () => (
  <div className="flex items-center gap-2 flex-shrink-0">
    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg transform rotate-2">
      <span className="text-white font-black text-2xl -rotate-2">V</span>
    </div>
    <span className="text-2xl font-black tracking-tighter text-gray-900">
      Ven<span className="text-emerald-500">dita</span>
    </span>
  </div>
);

const CopyableRow = ({ icon, label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Sin acceso al portapapeles (ej. http no seguro): no rompe nada, solo no copia.
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-100 rounded-2xl px-5 py-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-emerald-500 text-lg flex-shrink-0">
          {icon}
        </div>
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
          <p className="font-black text-gray-900 text-lg">{value}</p>
        </div>
      </div>
      <button
        onClick={handleCopy}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-all active:scale-95"
      >
        {copied ? <FaCheck className="text-emerald-500" /> : <FaCopy />}
        {copied ? "Copiado" : "Copiar"}
      </button>
    </div>
  );
};

const SupportPage = () => {
  const [companyName, setCompanyName] = useState("");
  const [qrError, setQrError] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("BASICO");
  const [billingCycle, setBillingCycle] = useState("mensual"); // "mensual" | "semestral" | "lifetime"

  const plan = PAID_PLANS[selectedPlan];
  const price = precioPlan(plan, billingCycle);
  const cicloTexto = billingCycle === "lifetime" ? "de por vida (pago único)" : billingCycle === "mensual" ? "por 1 mes" : "por 6 meses";
  const empresaTexto = companyName.trim() ? `de la compañía "${companyName.trim()}" ` : "";
  const waMessage = `Hola, soy ${empresaTexto}en Vendita. Ya hice el pago de ${price.toLocaleString("es-CO")} COP (plan ${plan.label}, ${cicloTexto}), les comparto el comprobante para activarlo 🚀`;

  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage)}`;

  return (
    <div className="min-h-screen w-full bg-white text-gray-900">
      <nav className="flex items-center justify-between px-6 py-6 max-w-3xl mx-auto w-full">
        <Link to="/" aria-label="Ir al inicio de Vendita">
          <Logo />
        </Link>
        <Link to="/" className="flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-gray-700 transition-colors">
          <FaArrowLeft /> Volver al inicio
        </Link>
      </nav>

      <div className="max-w-2xl mx-auto px-6 pb-24">
        {/* Header */}
        <div className="text-center pt-6 pb-10">
          <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-500 text-2xl">
            <FaHeart />
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-gray-900 mb-3">
            Actualiza tu plan en <span className="text-emerald-500">Vendita</span>
          </h1>
          <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
            Elige tu plan para seguir usando Vendita. Págalo en línea y se
            activa al instante, o por Nequi y te lo activamos al toque.
          </p>
        </div>

        {/* Ciclo de pago */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            {[
              { key: "mensual", label: "Mensual" },
              { key: "semestral", label: "6 meses" },
              { key: "lifetime", label: "De por vida" },
            ].map((c) => (
              <button
                key={c.key}
                onClick={() => setBillingCycle(c.key)}
                className={`px-5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${
                  billingCycle === c.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Selector de plan */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {Object.entries(PAID_PLANS).map(([key, p]) => (
            <button
              key={key}
              onClick={() => setSelectedPlan(key)}
              className={`text-left rounded-2xl border-2 p-5 transition-all ${
                selectedPlan === key
                  ? "border-emerald-500 bg-emerald-50/50 shadow-sm"
                  : "border-gray-100 bg-white hover:border-gray-200"
              }`}
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Plan {p.label}</p>
              <p className="text-2xl font-black text-gray-900 mb-1">
                ${precioPlan(p, billingCycle).toLocaleString("es-CO")}{" "}
                <span className="text-xs font-bold text-gray-400">COP</span>
              </p>
              <p className="text-xs text-gray-500 font-medium">
                Hasta {p.maxProducts} productos · {duracionTexto(p, billingCycle)}
              </p>
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 -mt-5 mb-8">
          Precio de lanzamiento — puede ajustarse más adelante, avisando antes a quienes ya pagaron.
        </p>

        <PagoEnLinea planKey={selectedPlan} plan={plan} ciclo={billingCycle} price={price} />

        {/* Pago manual por Nequi */}
        <h2 className="text-center font-black text-lg text-gray-900 mb-4">¿Prefieres pagar por Nequi?</h2>
        <div className="space-y-3 mb-8">
          <CopyableRow icon={<FaMobileAlt />} label="Nequi" value={NEQUI_NUMBER} />
        </div>

        {/* QR */}
        <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 mb-10 text-center">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4 flex items-center justify-center gap-2">
            <FaQrcode /> O escanea el QR (Nequi / Bre-B, desde cualquier app bancaria)
          </p>
          {!qrError ? (
            // La imagen (776x1600) trae mucho espacio en blanco: se muestra solo
            // la zona del QR (x 150-630, y 490-1160 px de la original) a mitad de
            // tamaño. Si se cambia el archivo por otro de otras medidas, hay que
            // ajustar este recorte.
            <div className="mx-auto overflow-hidden rounded-xl border border-gray-200 bg-white" style={{ width: 240, height: 335 }}>
              <img
                src="/qr-nequi.jpeg"
                alt="QR de Nequi para pagar tu plan de Vendita"
                onError={() => setQrError(true)}
                style={{ width: 388, maxWidth: "none", marginLeft: -75, marginTop: -245 }}
              />
            </div>
          ) : (
            <div className="w-48 h-48 mx-auto rounded-xl border-2 border-dashed border-gray-200 bg-white flex items-center justify-center text-gray-300 text-xs font-bold px-4 text-center">
              QR próximamente
            </div>
          )}
        </div>

        {/* Aviso por WhatsApp */}
        <div className="bg-gray-900 rounded-[2rem] p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 text-xl">
            <FaCrown />
          </div>
          <h3 className="text-white font-black text-xl mb-2">¿Ya hiciste el pago?</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Escríbenos por WhatsApp con el comprobante y el nombre de tu compañía
            registrada en Vendita, y te activamos el plan {plan.label} al toque.
          </p>

          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Nombre de tu compañía en Vendita (opcional)"
            className="w-full max-w-sm mx-auto block mb-4 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
          />

          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-emerald-500 text-white font-black px-8 py-4 rounded-2xl hover:bg-emerald-600 transition-all active:scale-95 shadow-lg shadow-emerald-500/20"
          >
            <FaWhatsapp size={20} /> Avisar por WhatsApp
          </a>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          Gracias por confiar en Vendita para tu negocio 💚
        </p>
      </div>
    </div>
  );
};

export default SupportPage;
